import React from 'react';
import { Box, Paper } from '@mui/material';
import { DocumentCustomization } from '../../types/documentCustomization.types';

interface DocumentPreviewProps {
  customization: DocumentCustomization;
  type: 'facture' | 'devis';
}

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({ customization, type }) => {
  const {
    logoUrl,
    logoWidth = 120,
    logoPosition = 'LEFT',
    primaryColor = '#2c3e50',
    secondaryColor = '#3498db',
    accentColor = '#27ae60',
    backgroundColor = '#ecf0f1',
    fontFamily = 'Helvetica',
    fontSize = 9,
    tableStyle = 'CLASSIC',
    showTableBorders = true,
    footerText,
    showLegalFooter = true,
    showPageNumber = true,
    pageMargin = 10,
    showBankDetails = true,
    showInsuranceSection = true,
    showQualifications = true,
    showPaymentConditions = true,
  } = customization;

  // CSS inline complet basé sur le template backend
  const cssStyles = `
    .preview-document * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .preview-document {
      font-family: '${fontFamily}', Arial, sans-serif;
      font-size: ${fontSize}pt;
      color: #333;
      line-height: 1.2;
      padding: ${pageMargin}mm 12mm 15mm 12mm;
      background-color: white;
      position: relative;
      min-height: 297mm;
      width: 210mm;
      margin: 0 auto;
    }

    /* En-tête avec logo intégré (comme dans les PDF) */
    .preview-document .header {
      width: 100%;
      border-bottom: 2px solid ${primaryColor};
      padding-bottom: 8px;
      margin-bottom: 12px;
    }

    .preview-document .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    .preview-document .header-logo {
      flex: 0 0 25%;
      padding-right: 10px;
    }

    .preview-document .header-logo.left {
      text-align: left;
    }

    .preview-document .header-logo.center {
      text-align: center;
    }

    .preview-document .header-logo.right {
      text-align: right;
    }

    .preview-document .header-logo img {
      max-width: ${logoWidth}px;
      max-height: 45px;
      vertical-align: middle;
      object-fit: contain;
      display: inline-block;
    }

    .preview-document .header-text {
      flex: 1;
      text-align: right;
    }

    .preview-document .header-text.full-width {
      flex: 1;
      text-align: left;
    }

    .preview-document .header h1 {
      font-size: 20pt;
      color: ${primaryColor};
      margin: 0 0 3px 0;
      font-weight: bold;
    }

    .preview-document .header-info {
      font-size: 10pt;
      color: #7f8c8d;
    }

    .preview-document .badge {
      display: inline-block;
      padding: 3px 8px;
      border-radius: 3px;
      font-size: 8pt;
      font-weight: bold;
      margin-left: 15px;
      background-color: ${secondaryColor};
      color: white;
    }

    /* Parties (Entreprise / Client) */
    .preview-document .parties {
      width: 100%;
      margin-bottom: 10px;
    }

    .preview-document .partie {
      width: 48%;
      float: left;
      padding: 8px;
      vertical-align: top;
    }

    .preview-document .partie:first-child {
      margin-right: 4%;
      background-color: #f8f9fa;
    }

    .preview-document .partie:last-child {
      border: 1px solid #ddd;
    }

    .preview-document .partie h3 {
      font-size: 9pt;
      color: ${primaryColor};
      margin-bottom: 5px;
      font-weight: bold;
      text-transform: uppercase;
    }

    .preview-document .partie p {
      margin: 3px 0;
      font-size: 9pt;
    }

    .preview-document .partie .nom {
      font-weight: bold;
      font-size: 10pt;
      color: #2c3e50;
    }

    .preview-document .clearfix::after {
      content: "";
      display: table;
      clear: both;
    }

    /* Informations du document */
    .preview-document .document-info {
      margin-bottom: 10px;
      padding: 6px 8px;
      background-color: #f8f9fa;
      border-left: 2px solid ${secondaryColor};
    }

    .preview-document .document-info p {
      margin: 2px 0;
      font-size: 8pt;
    }

    .preview-document .document-info .label {
      font-weight: bold;
      color: ${primaryColor};
      display: inline-block;
      width: 130px;
    }

    /* Tableau des lignes */
    .preview-document table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 12px;
    }

    .preview-document thead {
      background-color: ${primaryColor};
      color: white;
    }

    .preview-document thead th {
      padding: 6px 5px;
      text-align: left;
      font-size: 8pt;
      font-weight: bold;
      text-transform: uppercase;
    }

    .preview-document thead th.right {
      text-align: right;
    }

    .preview-document thead th.center {
      text-align: center;
    }

    .preview-document tbody tr {
      border-bottom: 1px solid #e0e0e0;
    }

    .preview-document tbody tr.ligne-principale {
      background-color: white;
    }

    .preview-document tbody tr.ligne-principale td {
      padding: 6px 5px;
      font-size: 8pt;
    }

    .preview-document tbody tr.ligne-principale td:last-child {
      font-weight: bold;
      color: #2c3e50;
    }

    .preview-document tbody tr.ligne-principale .label-service {
      display: inline-block;
      font-size: 6pt;
      font-weight: bold;
      color: ${primaryColor};
      text-transform: uppercase;
      margin-right: 5px;
    }

    .preview-document tbody tr.sous-ligne {
      background-color: #f5f6f7;
    }

    .preview-document tbody tr.sous-ligne td {
      padding: 5px 5px 5px 25px;
      font-size: 7pt;
      color: #7f8c8d;
    }

    .preview-document tbody tr.sous-ligne td:first-child::before {
      content: "→ ";
      margin-right: 3px;
    }

    .preview-document tbody tr.sous-ligne td:last-child {
      color: ${secondaryColor};
      font-weight: 500;
    }

    .preview-document tbody tr.sous-ligne .label-fourniture {
      display: inline-block;
      font-size: 6pt;
      font-weight: bold;
      color: #e67e22;
      text-transform: uppercase;
      margin-right: 5px;
    }

    .preview-document td.right {
      text-align: right;
    }

    .preview-document td.center {
      text-align: center;
    }

    /* Bloc totaux */
    .preview-document .totaux-container {
      width: 100%;
      text-align: right;
      margin-bottom: 15px;
    }

    .preview-document .totaux {
      display: inline-block;
      width: 300px;
      padding: 10px;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      background-color: white;
      text-align: left;
    }

    .preview-document .totaux table {
      width: 100%;
      margin: 0;
    }

    .preview-document .totaux tr {
      border-bottom: 1px solid #f0f0f0;
    }

    .preview-document .totaux tr:last-child {
      border-bottom: none;
    }

    .preview-document .totaux td {
      padding: 5px 4px;
      font-size: 9pt;
    }

    .preview-document .totaux td:first-child {
      color: #7f8c8d;
    }

    .preview-document .totaux td:last-child {
      text-align: right;
      font-weight: bold;
      color: #2c3e50;
    }

    .preview-document .totaux tr.total-ttc td:first-child {
      font-weight: bold;
      color: ${primaryColor};
      font-size: 10pt;
    }

    .preview-document .totaux tr.total-ttc td:last-child {
      background-color: ${primaryColor};
      color: white;
      font-size: 11pt;
      padding: 8px 12px;
      border-radius: 3px;
    }

    /* Sections conditionnelles */
    .preview-document .assurances,
    .preview-document .conditions-paiement,
    .preview-document .coordonnees-bancaires {
      margin-bottom: 12px;
      padding: 8px;
      border: 1px solid #4caf50;
      border-radius: 3px;
    }

    .preview-document .assurances {
      background-color: #e8f5e9;
    }

    .preview-document .conditions-paiement {
      background-color: #fff3e0;
      border-color: #ff9800;
    }

    .preview-document .coordonnees-bancaires {
      background-color: #e3f2fd;
      border-color: ${secondaryColor};
    }

    .preview-document .assurances h4,
    .preview-document .conditions-paiement h4,
    .preview-document .coordonnees-bancaires h4 {
      font-size: 9pt;
      margin-bottom: 4px;
      font-weight: bold;
    }

    .preview-document .assurances h4 {
      color: #2e7d32;
    }

    .preview-document .conditions-paiement h4 {
      color: #e65100;
    }

    .preview-document .coordonnees-bancaires h4 {
      color: #1565c0;
    }

    .preview-document .assurances p,
    .preview-document .conditions-paiement p,
    .preview-document .coordonnees-bancaires p {
      font-size: 7pt;
      margin: 2px 0;
      line-height: 1.3;
    }

    .preview-document .conditions-paiement .important {
      font-weight: bold;
      color: #d84315;
    }

    .preview-document .coordonnees-bancaires p {
      font-family: 'Courier New', monospace;
    }

    /* Signature (pour devis) */
    .preview-document .signature {
      margin-top: 15px;
      margin-bottom: 20px;
      padding: 10px;
      border: 1px dashed ${primaryColor};
      background-color: #f8f9fa;
    }

    .preview-document .signature h4 {
      font-size: 9pt;
      color: ${primaryColor};
      margin-bottom: 5px;
    }

    .preview-document .signature .mention {
      font-size: 7pt;
      color: #7f8c8d;
      font-style: italic;
      margin-bottom: 8px;
    }

    .preview-document .signature-fields {
      width: 100%;
    }

    .preview-document .signature-field {
      width: 48%;
      float: left;
      padding: 5px;
    }

    .preview-document .signature-field:first-child {
      margin-right: 4%;
    }

    .preview-document .signature-field p {
      margin: 2px 0;
      font-size: 8pt;
    }

    .preview-document .signature-line {
      margin-top: 25px;
      border-top: 1px solid #7f8c8d;
      padding-top: 3px;
      font-size: 6pt;
      color: #7f8c8d;
    }

    /* Footer */
    .preview-document .footer {
      margin-top: 20px;
      padding-top: 12px;
      border-top: 2px solid ${primaryColor};
      font-size: 7pt;
      color: #7f8c8d;
      text-align: center;
    }

    .preview-document .footer p {
      margin: 3px 0;
    }

    .preview-document .footer .custom-message {
      font-style: italic;
      color: ${primaryColor};
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 8pt;
    }

    /* Responsive pour mobile */
    @media (max-width: 768px) {
      .preview-document {
        width: 100%;
        min-width: unset;
        padding: 5mm;
        font-size: 7pt;
        overflow-x: hidden;
      }

      .preview-document .partie {
        width: 100%;
        float: none;
        margin-right: 0 !important;
        margin-bottom: 10px;
      }

      .preview-document .header-content {
        flex-direction: column !important;
      }

      .preview-document .header-logo {
        flex: unset !important;
        width: 100% !important;
        text-align: center !important;
        margin-bottom: 8px;
        padding-right: 0 !important;
      }

      .preview-document .header-text {
        flex: unset !important;
        width: 100% !important;
        text-align: center !important;
      }

      .preview-document .header h1 {
        font-size: 16pt;
      }

      .preview-document .header-logo img {
        max-width: 80px !important;
        max-height: 40px !important;
      }

      .preview-document .totaux {
        width: 100%;
      }

      .preview-document .signature-field {
        width: 100%;
        float: none;
        margin-right: 0 !important;
        margin-bottom: 10px;
      }

      .preview-document table {
        font-size: 6pt;
      }

      .preview-document thead th {
        padding: 4px 2px;
        font-size: 6pt;
      }

      .preview-document tbody td {
        padding: 4px 2px !important;
        font-size: 7pt !important;
      }
    }
  `;

  return (
    <Box sx={{ backgroundColor: '#e0e0e0', padding: { xs: 1, sm: 3 }, overflow: 'auto', maxHeight: '80vh', borderRadius: 2 }}>
      <style>{cssStyles}</style>
      <Paper elevation={3} sx={{ overflow: 'hidden' }}>
        <div className="preview-document">
          {/* En-tête avec logo intégré (comme dans les PDF) */}
          <div className="header">
            <div className="header-content">
              {/* Logo (si présent) */}
              {logoUrl && (
                <div className={`header-logo ${logoPosition.toLowerCase()}`}>
                  <img
                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`}
                    alt="Logo entreprise"
                    onError={(e) => {
                      console.error('Erreur chargement logo:', e);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Titre et informations du document */}
              <div className={`header-text ${!logoUrl ? 'full-width' : ''}`}>
                <h1>{type === 'facture' ? 'FACTURE' : 'DEVIS'}</h1>
                <div className="header-info">
                  <span>N° {type === 'facture' ? 'FACT' : 'DEV'}-2025-001</span>
                  <span className="badge">ENVOYÉ</span>
                </div>
              </div>
            </div>
          </div>

          {/* Entreprise et Client */}
          <div className="parties clearfix">
            <div className="partie">
              <h3>De :</h3>
              <p className="nom">Entreprise Carrelage Pro</p>
              <p>123 Avenue des Artisans</p>
              <p>75001 Paris</p>
              <p>Tél : 01 23 45 67 89</p>
              <p>Email : contact@entreprise.fr</p>
              <p>SIRET : 123 456 789 00012</p>
              <p>TVA : FR12345678901</p>
            </div>
            <div className="partie">
              <h3>À :</h3>
              <p className="nom">M. Jean Dupont</p>
              <p>Projet : Rénovation Salle de Bain</p>
            </div>
          </div>

          {/* Informations du document */}
          <div className="document-info">
            <p><span className="label">Date d'émission :</span> 14/01/2025</p>
            <p><span className="label">Date de {type === 'facture' ? 'échéance' : 'validité'} :</span> 13/02/2025</p>
            {type === 'facture' && (
              <p><span className="label">Type de facture :</span> Facture</p>
            )}
          </div>

          {/* Tableau des lignes */}
          <table>
            <thead>
              <tr>
                <th style={{ width: type === 'facture' ? '45%' : '50%' }}>DÉSIGNATION</th>
                <th className={type === 'facture' ? 'center' : 'right'} style={{ width: type === 'facture' ? '12%' : '15%' }}>
                  QUANTITÉ
                </th>
                {type === 'facture' && <th className="center" style={{ width: '10%' }}>UNITÉ</th>}
                <th className="right" style={{ width: type === 'facture' ? '13%' : '15%' }}>P.U. HT</th>
                {type === 'facture' && <th className="right" style={{ width: '10%' }}>TVA</th>}
                <th className="right" style={{ width: type === 'facture' ? '15%' : '20%' }}>TOTAL HT</th>
              </tr>
            </thead>
            <tbody>
              {/* Ligne principale */}
              <tr className="ligne-principale">
                <td>
                  <span className="label-service">Service</span>
                  <span>Pose de carrelage 60x60 - Sol cuisine</span>
                </td>
                <td className={type === 'facture' ? 'center' : 'right'}>25,00</td>
                {type === 'facture' && <td className="center">m²</td>}
                <td className="right">45,00 €</td>
                {type === 'facture' && <td className="right">20%</td>}
                <td className="right">1 125,00 €</td>
              </tr>

              {/* Sous-ligne */}
              <tr className="sous-ligne">
                <td>
                  <span className="label-fourniture">Fourniture</span>
                  <span>Mortier-colle C1/C2</span>
                </td>
                <td className={type === 'facture' ? 'center' : 'right'}>630</td>
                {type === 'facture' && <td className="center">kg</td>}
                <td className="right">2,50 €</td>
                {type === 'facture' && <td className="right">20%</td>}
                <td className="right">1 575,00 €</td>
              </tr>

              {/* Ligne principale 2 */}
              <tr className="ligne-principale">
                <td>
                  <span className="label-service">Service</span>
                  <span>Fourniture carrelage grès cérame premium</span>
                </td>
                <td className={type === 'facture' ? 'center' : 'right'}>25,00</td>
                {type === 'facture' && <td className="center">m²</td>}
                <td className="right">35,00 €</td>
                {type === 'facture' && <td className="right">20%</td>}
                <td className="right">875,00 €</td>
              </tr>

              {/* Ligne principale 3 */}
              <tr className="ligne-principale">
                <td>
                  <span className="label-service">Service</span>
                  <span>Préparation et ragréage du sol</span>
                </td>
                <td className={type === 'facture' ? 'center' : 'right'}>25,00</td>
                {type === 'facture' && <td className="center">m²</td>}
                <td className="right">12,00 €</td>
                {type === 'facture' && <td className="right">20%</td>}
                <td className="right">300,00 €</td>
              </tr>
            </tbody>
          </table>

          {/* Totaux */}
          <div className="totaux-container">
            <div className="totaux">
              <table>
                <tbody>
                  <tr>
                    <td>Sous-total HT</td>
                    <td>3 875,00 €</td>
                  </tr>
                  <tr>
                    <td>TVA (20%)</td>
                    <td>775,00 €</td>
                  </tr>
                  <tr className="total-ttc">
                    <td>TOTAL TTC</td>
                    <td>4 650,00 €</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="clearfix"></div>

          {/* Assurances obligatoires (Facture uniquement) */}
          {type === 'facture' && showInsuranceSection && (
            <div className="assurances">
              <h4>🛡️ Assurances Obligatoires</h4>
              <div>
                <p><span style={{ fontWeight: 'bold', color: '#2e7d32' }}>Assurance Décennale :</span> AXA France IARD</p>
                <p><span style={{ fontWeight: 'bold', color: '#2e7d32' }}>Police n° :</span> POL-2025-123456</p>
                <p><span style={{ fontWeight: 'bold', color: '#2e7d32' }}>Valable jusqu'au :</span> 31/12/2025</p>
              </div>
              <div style={{ marginTop: '8px' }}>
                <p><span style={{ fontWeight: 'bold', color: '#2e7d32' }}>RC Professionnelle :</span> Allianz</p>
                <p><span style={{ fontWeight: 'bold', color: '#2e7d32' }}>Police n° :</span> RC-2025-789012</p>
              </div>
            </div>
          )}

          {/* Conditions de paiement (Facture uniquement) */}
          {type === 'facture' && showPaymentConditions && (
            <div className="conditions-paiement">
              <h4>⚖️ Conditions de Paiement (Art. L.441-6 du Code de Commerce)</h4>
              <p>
                <span className="important">Échéance : </span>
                30 jours à compter de la date d'émission
              </p>
              <p>
                <span className="important">Pénalités de retard : </span>
                12% (3 fois le taux d'intérêt légal) applicables dès le lendemain de la date d'échéance
              </p>
              <p>
                <span className="important">Indemnité forfaitaire pour frais de recouvrement : </span>
                40,00 € (Art. D.441-5)
              </p>
            </div>
          )}

          {/* Coordonnées bancaires (Facture uniquement) */}
          {type === 'facture' && showBankDetails && (
            <div className="coordonnees-bancaires">
              <h4>💳 Coordonnées Bancaires</h4>
              <p><strong>IBAN :</strong> FR76 1234 5678 9012 3456 7890 123</p>
              <p><strong>BIC :</strong> BNPAFRPPXXX</p>
              <p style={{ fontFamily: 'Arial, sans-serif' }}><strong>Banque :</strong> BNP Paribas</p>
            </div>
          )}

          {/* Zone de signature (Devis uniquement) */}
          {type === 'devis' && (
            <div className="signature">
              <h4>Bon pour accord</h4>
              <p className="mention">
                En signant ce devis, le client reconnaît en avoir pris connaissance et l'accepte sans réserve.
              </p>
              <div className="signature-fields clearfix">
                <div className="signature-field">
                  <p><strong>Client :</strong></p>
                  <p>Date : _____ / _____ / __________</p>
                  <div className="signature-line">
                    Signature précédée de la mention "Bon pour accord"
                  </div>
                </div>
                <div className="signature-field">
                  <p><strong>Entreprise Carrelage Pro</strong></p>
                  <p>Date : 14/01/2025</p>
                  <div className="signature-line">
                    Signature
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="clearfix"></div>

          {/* Footer */}
          {showLegalFooter && (
            <div className="footer">
              {footerText && (
                <p className="custom-message">{footerText}</p>
              )}
              <div>
                <p>
                  <span>Entreprise Carrelage Pro</span> - SIRET : 123 456 789 00012
                </p>
                <p>TVA : FR12345678901</p>
                <p>
                  ✅ Garantie Décennale : AXA France IARD (Police POL-2025-123456)
                </p>
                <p style={{ marginTop: '5px', fontSize: '6pt' }}>
                  Médiateur : CNPM - www.cnpm-mediation-consommation.eu
                </p>
                <p style={{ marginTop: '5px', fontSize: '6pt', lineHeight: '1.3' }}>
                  Travaux soumis aux garanties légales (parfait achèvement 1 an, biennale 2 ans, décennale 10 ans).
                </p>
              </div>
              {showPageNumber && (
                <p style={{ marginTop: '5px' }}>Document généré le 14/01/2025</p>
              )}
            </div>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default DocumentPreview;
